import { useNavigate } from 'react-router-dom';
import useSchools from '../hooks/useSchools';
import useUserDetails from '../hooks/useUserDetails';
import useErrorModal from '../hooks/useErrorModal';
import CustomButton from '../reusable/CustomButton';
import Loader from '../reusable/Loader';
import GoBackIcon from '../assets/transcript-selection-arrow-left.svg';
import { useTranscriptSelectionOutletContext } from '../hooks/useTranscriptSelectionOutletContext';
import { useState } from 'react';
import { USER_ROLES, TRANSCRIPTS_CATEGORIES_SELECTION_LIMITS, TRANSCRIPTS_CATEGORIES } from '../../constants';
import TranscriptSelectionWarningModal from './TranscriptSelectionWarningModal';
import TranscriptReviewModal from './TranscriptReviewModal';
import { executeTranscriptSelectionWorkflowTransition } from '../../services/workflowService';
import TranscriptSelectionStatus from './TranscriptSelectionStatus';
import { AxiosError } from 'axios';
import TranscriptsPrintByStudentButton from './print/TranscriptsPrintByStudentButton';
import TranscriptsPrintBySubjectButton from './print/TranscriptsPrintBySubjectButton';

import './TranscriptSelectionHeader.css';

const selectionLimits = [
  TRANSCRIPTS_CATEGORIES_SELECTION_LIMITS.RIGOROUS_ACADEMIC,
  TRANSCRIPTS_CATEGORIES_SELECTION_LIMITS.ON_THE_CUSP,
  TRANSCRIPTS_CATEGORIES_SELECTION_LIMITS.STRUGGLING,
];

interface TranscriptSelectionHeaderProps {
  handleSideDrawerOpen: () => void;
}

function TranscriptSelectionHeader({ handleSideDrawerOpen }: TranscriptSelectionHeaderProps) {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isReopenModalOpen, setIsReopenModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const {
    userDetails: { data: userDetails },
  } = useUserDetails();
  const {
    schools: { isPending, error, data: schools },
    transcriptSelectionSchool,
  } = useSchools();
  const { handleOpen: handleOpenErrorModal } = useErrorModal();
  const {
    selectedTranscriptsSummaryResponse: { selectedTranscriptsSummary },
    workflowStatus,
    refreshWorkflowStatus,
    refreshData,
  } = useTranscriptSelectionOutletContext();

  const selectedTranscriptsByCategory = selectedTranscriptsSummary.selectedTranscriptsByCategory;

  const allSelectedTranscripts = Object.values(selectedTranscriptsByCategory).flat();

  const selectionCounts = [
    selectedTranscriptsByCategory[TRANSCRIPTS_CATEGORIES.RIGOROUS_ACADEMIC].length,
    selectedTranscriptsByCategory[TRANSCRIPTS_CATEGORIES.ON_THE_CUSP].length,
    selectedTranscriptsByCategory[TRANSCRIPTS_CATEGORIES.STRUGGLING].length,
  ];

  const isCompleteSelectionDisabled = selectionCounts.some((count, index) => selectionLimits[index] !== count);

  const userRole = userDetails?.role;
  const isRestrictedView = userRole === USER_ROLES.XQ_USER;

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleWorkflowTransition = async (transitionName: string, closeModal: () => void, errorMessage: string) => {
    if (!userDetails) return;

    setIsProcessing(true);
    try {
      await executeTranscriptSelectionWorkflowTransition(
        userDetails.eoaId,
        transcriptSelectionSchool,
        userDetails.access_token,
        transitionName
      );
      await refreshWorkflowStatus();
      await refreshData();
      closeModal();
    } catch (error) {
      console.error(`Error ${transitionName}:`, error);
      handleOpenErrorModal(errorMessage, error as AxiosError);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleApprove = () =>
    handleWorkflowTransition(
      'approve_transcript_selection',
      () => setIsApproveModalOpen(false),
      'Error occurred while approving transcript selection!'
    );

  const handleReject = () =>
    handleWorkflowTransition(
      'reject_transcript_selection',
      () => setIsRejectModalOpen(false),
      'Error occurred while rejecting transcript selection!'
    );

  const handleReopen = () =>
    handleWorkflowTransition(
      'reopen_transcript_selection',
      () => setIsReopenModalOpen(false),
      'Error occurred while reopening transcript selection!'
    );

  if (isPending) return <Loader />;

  const isLeadFacilitatorOrAdmin = userRole === USER_ROLES.XQ_ADMIN || userRole === USER_ROLES.LEAD_FACILITATOR;
  const isFacilitator = userRole === USER_ROLES.FACILITATOR;
  const isLeadFacilitator = userRole === USER_ROLES.LEAD_FACILITATOR;
  const isAdmin = userRole === USER_ROLES.XQ_ADMIN;
  const showReviewButtons = isLeadFacilitatorOrAdmin && workflowStatus === 'TRANSCRIPT_SELECTION_COMPLETED';
  const showCompleteSelectionsButton =
    (workflowStatus === 'TRANSCRIPT_SELECTION_IN_PROGRESS' || workflowStatus === 'TRANSCRIPT_SELECTION_NOT_STARTED') &&
    (isFacilitator || isLeadFacilitator) &&
    !isRestrictedView;
  const showReopenButton = isAdmin && workflowStatus === 'TRANSCRIPT_SELECTION_FINALIZED';

  const currentSchool = schools?.find((school) => school.id === transcriptSelectionSchool);

  return (
    <>
      <TranscriptSelectionWarningModal isOpen={isModalOpen} handleClose={handleCloseModal} />
      <TranscriptReviewModal
        isOpen={isApproveModalOpen}
        handleClose={() => setIsApproveModalOpen(false)}
        handleConfirm={handleApprove}
        type="approve"
        isLoading={isProcessing}
      />
      <TranscriptReviewModal
        isOpen={isRejectModalOpen}
        handleClose={() => setIsRejectModalOpen(false)}
        handleConfirm={handleReject}
        type="reject"
        isLoading={isProcessing}
      />
      <TranscriptReviewModal
        isOpen={isReopenModalOpen}
        handleClose={() => setIsReopenModalOpen(false)}
        handleConfirm={handleReopen}
        type="reopen"
        isLoading={isProcessing}
      />
      <div className="transcript-selection-page-container">
        <CustomButton
          variant="text"
          buttonText="Back"
          icon={<img src={GoBackIcon} alt="go back" />}
          iconPosition="start"
          buttonAction={() => navigate('/transcript-selection')}
        />
        <div className="transcript-selection-header">
          {error ? <p>Missing schools data!</p> : null}
          <div className="transcript-selection-header-title">
            <h1 className="transcript-selection-school-name">{currentSchool?.name || 'School'}</h1>
            <TranscriptSelectionStatus status={workflowStatus} />
          </div>
          <div className="transcript-selection-header-actions">
            {showReopenButton && (
              <CustomButton
                variant="text"
                buttonText="Re-open transcript selection"
                buttonAction={() => setIsReopenModalOpen(true)}
                disabled={isProcessing}
              />
            )}
            {isLeadFacilitatorOrAdmin && workflowStatus === 'TRANSCRIPT_SELECTION_FINALIZED' && (
              <>
                <TranscriptsPrintByStudentButton transcripts={allSelectedTranscripts} />
                <TranscriptsPrintBySubjectButton />
              </>
            )}
            <CustomButton variant="secondary" buttonText="View demographics" buttonAction={handleSideDrawerOpen} />
            {showReviewButtons && (
              <>
                <CustomButton
                  variant="secondary"
                  buttonText="Reject selection"
                  buttonAction={() => setIsRejectModalOpen(true)}
                  disabled={isProcessing}
                />
                <CustomButton
                  variant="primary"
                  buttonText="Approve selection"
                  buttonAction={() => setIsApproveModalOpen(true)}
                  disabled={isProcessing}
                />
              </>
            )}
            {showCompleteSelectionsButton && (
              <CustomButton
                variant="primary"
                buttonText="Complete selections"
                disabled={isCompleteSelectionDisabled}
                buttonAction={() => setIsModalOpen(true)}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default TranscriptSelectionHeader;
